import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, EMPTY, map, mergeMap, Observable, of, tap } from 'rxjs';
import { LuxonUtils } from '../../shared/utils/luxon-utils';
import { SetUtils } from '../../shared/utils/set-utils';
import { CoreItemType, VertexRelation, VertexRelationAdd, VertexRelationType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { RelationService } from '../relation/relation.service';
import { Session } from '../session/session';
import { Task, TaskType } from './task';
@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private _changeSubject = new BehaviorSubject<Task | Task[] | undefined>(undefined);

  constructor(
    private relationService: RelationService,
    private coreItemService: CoreItemService
  ) {}

  getAssignedBoardIdList(taskId: string) {
    return this.relationService
      .getRelationListByToId(taskId, {
        fromType: CoreItemType.board,
        relationType: VertexRelationType.has,
      })
      .map((c) => c.from);
  }

  assignTaskToBoardList$(taskId: string, boardIdList: string[]) {
    const addRelationList: VertexRelation[] = [];

    for (const boardId of boardIdList) {
      addRelationList.push({
        from: boardId,
        to: taskId,
        relation: VertexRelationType.has,
      });
    }

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addRelationList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next([]);
        })
      );
  }

  unassignBoardIdListFromTask$(taskId: string, boardIdList: string[]) {
    const deleteRelationList: VertexRelation[] = [];

    for (const boardId of boardIdList) {
      deleteRelationList.push({
        from: boardId,
        to: taskId,
        relation: VertexRelationType.has,
      });
    }

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteRelationList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next([]);
        })
      );
  }

  getTaskList$(boardIdList?: string[]) {
    if (!boardIdList) {
      return EMPTY;
    }

    if (boardIdList.length === 0) {
      return EMPTY;
    }

    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        let relationList: VertexRelation[] = [];

        for (const boardId of boardIdList) {
          relationList = relationList.concat(
            this.relationService.getRelationListByFromId(boardId, {
              relationType: VertexRelationType.has,
              toType: CoreItemType.task,
            })
          );
        }

        const taskIdList = relationList.map((relation) => relation.to);

        return (this.coreItemService.getCoreItemListByIdList$(taskIdList) as Observable<Task[]>).pipe(
          mergeMap((taskList) => {
            let sessionTemplateIdList: string[] = [];

            for (const task of taskList) {
              const relationList = this.relationService.getRelationListByFromId(task.id, {
                relationType: VertexRelationType.has,
                toType: CoreItemType.sessionTemplate,
              });

              const tempSessionTemplateIdList = relationList.map((relation) => relation.to);

              sessionTemplateIdList = sessionTemplateIdList.concat(tempSessionTemplateIdList);
            }

            if (sessionTemplateIdList.length === 0) {
              return of(taskList);
            }

            let timeRange = LuxonUtils.findTimeRange(taskList);

            if (!timeRange) {
              timeRange = { earliestStartTime: DateTime.local().minus({ months: 3 }), latestEndTime: DateTime.local() };
            }

            return this.coreItemService
              .getCoreItemList$({
                coreTypeList: [CoreItemType.session],
                parentIdList: sessionTemplateIdList,
                dateRange: { startTime: timeRange.earliestStartTime, endTime: timeRange.latestEndTime },
              })
              .pipe(
                map((sessionList) => {
                  for (const task of taskList) {
                    if (!task.frontendData) {
                      task.frontendData = {};
                    }

                    if (!task.frontendData.sessionTemplateIdAndSessionIdSet) {
                      task.frontendData.sessionTemplateIdAndSessionIdSet = new Map<string, Set<string>>();
                    }

                    const relationList = this.relationService.getRelationListByFromId(task.id, {
                      relationType: VertexRelationType.has,
                      toType: CoreItemType.sessionTemplate,
                    });

                    const tempSessionTemplateIdSet = SetUtils.fromArray(relationList.map((relation) => relation.to));

                    tempSessionTemplateIdSet.forEach((sessionTemplateId) => {
                      if (!task.frontendData!.sessionTemplateIdAndSessionIdSet!.has(sessionTemplateId)) {
                        task.frontendData!.sessionTemplateIdAndSessionIdSet!.set(sessionTemplateId, new Set<string>());
                      }
                    });

                    for (const session of sessionList as Session[]) {
                      if (task.frontendData.sessionTemplateIdAndSessionIdSet) {
                        if (session.parentId) {
                          if (tempSessionTemplateIdSet.has(session.parentId)) {
                            task.frontendData.sessionTemplateIdAndSessionIdSet.get(session.parentId)?.add(session.id);
                          }
                        }
                      }
                    }
                  }

                  taskList.sort((a, b) =>
                    (a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1
                  );
                  return taskList;
                })
              );
          })
        );
      })
    );
  }

  updateTask$(task: Task) {
    return this.coreItemService.updateCoreItem$(task).pipe(
      tap((data) => {
        this._changeSubject.next(data as Task);
      })
    );
  }

  /*
    Example:
    Task01 - Assign 3 scenario to 5 users.

    user01 has Task01 [session01, session02]
    user02 has Task01
    user03 has Task01

    task01 has scenario01
    task01 has scenario02
    task01 has scenario03

    User01 -> Task01 -> scenario01 / scenario02 / scenario03
  */
  addTargetTask$(
    title: string,
    startTime: DateTime,
    endTime: DateTime,
    targetIdList: string[],
    assignToIdList: string[]
  ) {
    if (targetIdList.length === 0 || assignToIdList.length === 0) {
      return EMPTY;
    }

    // 1. Create Task
    // 2. Create Board [AssignToId] has Task
    // 3. Create Task has Target [targetId]
    const addRelationList: VertexRelationAdd[] = [];

    for (const assignToId of assignToIdList) {
      addRelationList.push({
        from: assignToId,
        relation: VertexRelationType.has,
      });
    }

    for (const targetId of targetIdList) {
      addRelationList.push({
        to: targetId,
        relation: VertexRelationType.has,
      });
    }

    const addItemList = {
      item: { title, type: TaskType.target, startTime, endTime },
      coreType: CoreItemType.task,
      addRelationList: addRelationList,
    };

    return this.coreItemService.addItemList$([addItemList]).pipe(
      tap((data) => {
        this._changeSubject.next(data as Task[]);
      })
    );
  }

  // getTaskListByBoardId$(boardId: string) {
  //   const request = {
  //     boardId,
  //   };
  //   return this._changeSubject.asObservable().pipe(
  //     mergeMap((_) => {
  //       return this.coreItemService.getCoreItemListByAction$(TaskGate.actions.getTaskListByBoardId, request).pipe(
  //         map((list) => list.filter((item) => item.coreType === CoreItemType.task && ) as Methodology[]),
  //         map((list) =>
  //           list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1))
  //         )
  //       );
  //     })
  //   );
  // }
}
